import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * タブ。
 *
 * `Segmented` と見た目は同じだが、こちらは中身 (tabpanel) と
 * `aria-controls` で結ぶので aria の構造が違う。4 個以上並べる前提でもある。
 * 「見る軸」のような表示の切り替えは `Segmented` のまま。
 */
export function Tabs<T extends string>({
  value,
  onChange,
  options,
  label,
  className,
}: {
  value: T;
  onChange: (value: T) => void;
  options: Array<{ value: T; label: string }>;
  /** スクリーンリーダー向けの群の名前 */
  label: string;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn('flex flex-wrap border border-line-strong', className)}
    >
      {options.map((option, index) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          id={`tab-${option.value}`}
          aria-selected={value === option.value}
          aria-controls={`tabpanel-${option.value}`}
          onClick={() => onChange(option.value)}
          className={cn(
            'px-3 py-1.5 text-xs',
            index > 0 && 'border-l border-line-strong',
            value === option.value
              ? 'bg-brand-700 text-surface-raised'
              : 'text-ink-muted hover:bg-surface',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function TabPanel({ value, children }: { value: string; children: ReactNode }) {
  return (
    <div role="tabpanel" id={`tabpanel-${value}`} aria-labelledby={`tab-${value}`}>
      {children}
    </div>
  );
}
